/**
 * Å laste Google Maps én gang.
 *
 * Skriptet kan bare lastes én gang per side. Kartsida har to kart (oversikten
 * og punktvelgeren i portalen), og React kan montere dem om igjen når som
 * helst – så lasteren må tåle å bli kalt mange ganger og likevel bare gjøre
 * jobben én gang. Det er hele grunnen til at denne fila finnes.
 *
 * Løftet lagres, ikke resultatet. Blir det kalt to ganger før skriptet er nede,
 * venter begge på det samme.
 */

export type Kartoppsett = {
  nokkel: string;
  kartId: string;
  manglerKartId: boolean;
};

/** Hva som gikk galt, i klartekst. Vises til brukeren, ikke bare i konsollen. */
export type Kartfeil = 'ingen-nokkel' | 'avvist-nokkel' | 'lastefeil' | null;

let lastet: Promise<typeof google.maps> | null = null;

/**
 * Google melder avviste nøkler ved å kalle denne på `window`.
 *
 * Det er den eneste måten å få vite at nøkkelen er feil, utløpt, ubetalt eller
 * begrenset til et annet domene. Uten dette er svaret et grått kart med
 * «For development purposes only» – eller ingenting – og ingen beskjed.
 */
let avvist = false;
export function nokkelenBleAvvist(): boolean {
  return avvist;
}

declare global {
  interface Window {
    gm_authFailure?: () => void;
  }
}

export function lastGoogleMaps(oppsett: Kartoppsett): Promise<typeof google.maps> {
  if (lastet) return lastet;

  lastet = new Promise((løs, avvisLøftet) => {
    if (!oppsett.nokkel) {
      avvisLøftet(new Error('ingen-nokkel'));
      return;
    }

    window.gm_authFailure = () => {
      avvist = true;
      // Google gir ingen detaljer her. Meldinga i grensesnittet peker derfor på
      // de tre tingene det pleier å være.
      console.error(
        '[kart] Google avviste nøkkelen. Sjekk at den er gyldig, at fakturering ' +
          'er på, og at domenet står i henvisningsbegrensningene.',
      );
    };

    const skript = document.createElement('script');
    // `loading=async` er måten Google vil ha det på nå; uten den skriver de en
    // advarsel i konsollen ved hver last.
    const p = new URLSearchParams({
      key: oppsett.nokkel,
      v: 'weekly',
      libraries: 'maps,marker',
      language: 'no',
      region: 'NO',
      loading: 'async',
      callback: '__kartKlart',
    });
    skript.src = `https://maps.googleapis.com/maps/api/js?${p}`;
    skript.async = true;

    (window as unknown as Record<string, unknown>).__kartKlart = () => {
      løs(google.maps);
    };
    skript.onerror = () => avvisLøftet(new Error('lastefeil'));

    document.head.append(skript);
  });

  return lastet;
}

/**
 * Nåla, tegnet i koden.
 *
 * Samme form og samme farge som før, så kartet ser likt ut selv om det som
 * ligger under, er byttet. Google tar imot et vanlig DOM-element, så SVG-en
 * kan gjenbrukes rett fra Leaflet-utgaven.
 */
export function lagNaal(valgt: boolean): HTMLElement {
  const boks = document.createElement('div');
  boks.className = `kartnaal${valgt ? ' valgt' : ''}`;
  boks.innerHTML = `<svg viewBox="0 0 24 32" width="26" height="34" aria-hidden="true">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20c0-6.6-5.4-12-12-12z"/>
      <circle cx="12" cy="12" r="4.5" class="kjerne"/>
    </svg>`;
  return boks;
}

/** Norge, sånn omtrent. Der kartet står når ingenting annet er valgt. */
export const NORGE = { nord: 71.2, sor: 57.9, ost: 31.2, vest: 4.5 };
